import { useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  incrementVasCounter,
  incrementProductCounter,
  markVasTriggeredPage,
  markProductTriggeredPage,
} from "~/redux/actions/chatbot";
import { selectChatbotState } from "~/redux/selectors/chatbot";

interface ThresholdConfig {
  vasThreshold: number | undefined;
  productThreshold: number | undefined;
  currentPage: string;
}

interface ThresholdTracking {
  trackVasAction: () => boolean; // Returns true if threshold met and trigger should fire
  trackProductSwitch: () => boolean; // Returns true if threshold met and trigger should fire
}

/**
 * Hook for tracking chatbot trigger thresholds
 * - Session-wide counter tracking using Redux
 * - Per-page trigger limiting (only fire once per page when threshold met)
 */
export const useChatThresholdTracking = ({
  vasThreshold,
  productThreshold,
  currentPage,
}: ThresholdConfig): ThresholdTracking => {
  const dispatch = useDispatch();

  // Select chatbot state from Redux with fallback to defaults
  const chatbotState = useSelector(selectChatbotState);

  // Use refs to avoid recreating callbacks
  const configRef = useRef({ vasThreshold, productThreshold, currentPage });
  configRef.current = { vasThreshold, productThreshold, currentPage };

  /**
   * Track a VAS action (select/deselect)
   * Returns true if threshold is met and trigger should fire (only once per page)
   */
  const trackVasAction = useCallback((): boolean => {
    const { vasThreshold, currentPage } = configRef.current;

    // Exit early if threshold is not configured
    if (vasThreshold === undefined) {
      return false;
    }

    // Check if this page has already been triggered
    if (chatbotState.vasTriggeredPages[currentPage] === true) {
      return false;
    }

    // Increment counter
    dispatch(incrementVasCounter());
    const newCount = chatbotState.vasCounter + 1;

    // Check if threshold is met
    if (newCount >= vasThreshold) {
      dispatch(markVasTriggeredPage(currentPage));
      return true;
    }

    return false;
  }, [chatbotState.vasCounter, chatbotState.vasTriggeredPages, dispatch]);

  /**
   * Track a product switch action (plan or recommendation package change)
   * Returns true if threshold is met and trigger should fire (only once per page)
   */
  const trackProductSwitch = useCallback((): boolean => {
    const { productThreshold, currentPage } = configRef.current;

    // Exit early if threshold is not configured
    if (productThreshold === undefined) {
      return false;
    }

    // Check if this page has already been triggered
    if (chatbotState.productTriggeredPages[currentPage] === true) {
      return false;
    }

    // Increment counter
    dispatch(incrementProductCounter());
    const newCount = chatbotState.productCounter + 1;

    // Check if threshold is met
    if (newCount >= productThreshold) {
      dispatch(markProductTriggeredPage(currentPage));
      return true;
    }

    return false;
  }, [chatbotState.productCounter, chatbotState.productTriggeredPages, dispatch]);

  return {
    trackVasAction,
    trackProductSwitch,
  };
};
